import { useState, useEffect, useRef } from 'react';
import {
  FileText, Download, Building2, CheckCircle, Info,
  Loader, Pen, Trash2, Upload, User, CreditCard, Phone, X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { bankApi, Bank } from '@/api/banks';
import { customerApi } from '@/api/customers';
import { apiClient } from '@/api/client';
import toast from 'react-hot-toast';

const FAMILIENSTAND_OPTIONS = [
  { value: 'ledig', label: 'Ledig' },
  { value: 'verheiratet', label: 'Verheiratet' },
  { value: 'Lebensgemeinschaft', label: 'Lebensgemeinschaft' },
  { value: 'getrennt lebend', label: 'Getrennt lebend' },
  { value: 'geschieden', label: 'Geschieden' },
  { value: 'verwitwet', label: 'Verwitwet' },
];

const BERUFSGRUPPE_OPTIONS = [
  { value: 'Angestellte/-r', label: 'Angestellte/-r' },
  { value: 'selbständig Erwerbstätige/-r', label: 'Selbständig Erwerbstätige/-r' },
  { value: 'Arbeiter/-in', label: 'Arbeiter/-in' },
  { value: 'Beamter/Beamtin', label: 'Beamter/Beamtin' },
  { value: 'Freiberufler/-in', label: 'Freiberufler/-in' },
  { value: 'Facharbeiter/-in', label: 'Facharbeiter/-in' },
  { value: 'Hausfrau/Hausmann', label: 'Hausfrau/Hausmann' },
  { value: 'Pensionist/-in, Rentner/-in', label: 'Pensionist/-in, Rentner/-in' },
  { value: 'Student/-in, Schüler/-in', label: 'Student/-in, Schüler/-in' },
  { value: 'ohne Beschäftigung', label: 'Ohne Beschäftigung' },
];

interface SavedDoc {
  id: number;
  bank_name: string;
  laufzeit: number;
  zinssatz: number;
  anlagebetrag: string;
  familienstand: string;
  berufsgruppe: string;
  iban: string;
  kunden_nr: string;
  filename: string;
  created_at: string;
  doc_type?: string;
  description?: string;
}

const SectionTitle = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 pb-2 border-b border-slate-200 mb-4">
    <Icon className="w-4 h-4 text-primary-600" />
    <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</h2>
  </div>
);

const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
      {label}{required && <span className="text-danger-600 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500";

const DocumentsPage = () => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminCreated, setIsAdminCreated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{ name: string }[]>([]);
  const [savedDocs, setSavedDocs] = useState<SavedDoc[]>([]);
  const [imagePreview, setImagePreview] = useState<{ url: string; name: string } | null>(null);
  const [isDownloading, setIsDownloading] = useState<number | null>(null);

  // ── Section 1: Persönliche Daten ──
  const [anrede, setAnrede] = useState('herr');
  const [titel, setTitel] = useState('');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [strasseNr, setStrasseNr] = useState('');
  const [plz, setPlz] = useState('');
  const [ort, setOrt] = useState('');
  const [nationalitaet, setNationalitaet] = useState('Deutsch');
  const [geburtsdatum, setGeburtsdatum] = useState('');
  const [telNr, setTelNr] = useState('');
  const [email, setEmail] = useState('');

  // ── Section 2: Familienstand & Beruf ──
  const [familienstand, setFamilienstand] = useState('ledig');
  const [berufsgruppe, setBerufsgruppe] = useState('Angestellte/-r');

  // ── Section 3: Referenzkonto ──
  const [iban, setIban] = useState('');
  const [refBankName, setRefBankName] = useState('');

  // ── Section 4: Anlagedetails ──
  const [selectedBank, setSelectedBank] = useState<number | ''>('');
  const [laufzeit, setLaufzeit] = useState('12');
  const [zinssatz, setZinssatz] = useState('');
  const [anlagebetrag, setAnlagebetrag] = useState('');

  // ── Section 5: Signature ──
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  const loadSavedDocs = async () => {
    try {
      const res = await apiClient.get('/api/v1/pdf/my-documents');
      setSavedDocs(res.data?.documents || []);
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [banksRes, profileRes] = await Promise.all([
          bankApi.list({ is_active: true, page_size: 50 }),
          customerApi.profile.get(),
        ]);
        setBanks(banksRes.banks || []);
        if (profileRes) {
          if (profileRes.admin_created) setIsAdminCreated(true);
          if ((profileRes as any).titel) setTitel((profileRes as any).titel);
          if (profileRes.first_name) setVorname(profileRes.first_name);
          if (profileRes.last_name) setNachname(profileRes.last_name);
          if (profileRes.address) setStrasseNr(profileRes.address);
          if (profileRes.postal_code) setPlz(profileRes.postal_code);
          if (profileRes.city) setOrt(profileRes.city);
          if (profileRes.country) setNationalitaet(profileRes.country);
          if ((profileRes as any).birth_date) setGeburtsdatum((profileRes as any).birth_date);
          if (profileRes.phone) setTelNr(profileRes.phone);
          if ((profileRes as any).email) setEmail((profileRes as any).email);
        }
      } catch {
        toast.error('Daten konnten nicht geladen werden.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
    loadSavedDocs();
  }, []);

  // ── Signature ──────────────────────────────────────────────────────────────
  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    if ('touches' in e) {
      const t = e.touches[0];
      return { x: (t.clientX - rect.left) * sx, y: (t.clientY - rect.top) * sy };
    }
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getPos(e);
    if (!pos) return;
    setIsDrawing(true);
    lastPos.current = pos;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) { ctx.beginPath(); ctx.arc(pos.x, pos.y, 1, 0, Math.PI * 2); ctx.fillStyle = '#1a1a2e'; ctx.fill(); }
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing || !lastPos.current) return;
    const pos = getPos(e);
    if (!pos) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y); ctx.lineTo(pos.x, pos.y);
      ctx.strokeStyle = '#1a1a2e'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.stroke();
      setHasSignature(true);
    }
    lastPos.current = pos;
  };

  const stopDraw = (e: React.MouseEvent | React.TouchEvent) => { e.preventDefault(); setIsDrawing(false); lastPos.current = null; };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // ── File upload ────────────────────────────────────────────────────────────
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        await apiClient.post('/api/v1/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setUploadedFiles(prev => [...prev, { name: file.name }]);
      }
      toast.success('Dokument(e) erfolgreich hochgeladen');
    } catch {
      toast.error('Datei konnte nicht hochgeladen werden.');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  // ── PDF generation ─────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!vorname.trim() || !nachname.trim()) { toast.error('Bitte Vor- und Nachname eingeben.'); return; }
    if (!selectedBank) { toast.error('Bitte wählen Sie eine Bank aus.'); return; }
    if (!zinssatz) { toast.error('Bitte geben Sie den Zinssatz ein.'); return; }
    if (!anlagebetrag) { toast.error('Bitte geben Sie den Anlagebetrag ein.'); return; }

    // Capture signature as base64 PNG
    const signatureBase64 = hasSignature && canvasRef.current
      ? canvasRef.current.toDataURL('image/png')
      : null;

    setIsGenerating(true);
    try {
      const response = await apiClient.post(
        '/api/v1/pdf/citibank/me',
        {
          bank_id: Number(selectedBank),
          laufzeit: Number(laufzeit),
          zinssatz: parseFloat(zinssatz),
          anlagebetrag,
          familienstand,
          berufsgruppe,
          iban: iban.trim(),
          ref_bank_name: refBankName.trim(),
          anrede,
          titel,
          vorname_override: vorname.trim() || null,
          nachname_override: nachname.trim() || null,
          strasse_nr: strasseNr.trim() || null,
          plz: plz.trim() || null,
          ort: ort.trim() || null,
          nationalitaet: nationalitaet.trim() || null,
          geburtsdatum: geburtsdatum || null,
          tel_nr: telNr.trim() || null,
          email_override: email.trim() || null,
          signature_base64: signatureBase64,
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const bankName = banks.find(b => b.id === Number(selectedBank))?.name || 'Bank';
      a.download = `Festgeldantrag_${bankName}_${nachname}`.replace(/\s+/g, '_') + '.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF wurde erfolgreich erstellt und gespeichert!');
      loadSavedDocs(); // refresh list
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'PDF konnte nicht erstellt werden.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Download saved doc ─────────────────────────────────────────────────────
  const handleDownloadSaved = async (doc: SavedDoc) => {
    setIsDownloading(doc.id);
    try {
      const response = await apiClient.get(`/api/v1/pdf/download/${doc.id}`, { responseType: 'blob' });
      const isImage = /\.(png|jpe?g|webp|gif)$/i.test(doc.filename);
      const mime = isImage
        ? (doc.filename.match(/\.(png|jpe?g|webp|gif)$/i)?.[0].toLowerCase() === '.png' ? 'image/png' : 'image/jpeg')
        : 'application/pdf';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      if (isImage) {
        // Resimleri uygulama içi modalda göster (mobil popup engeline takılmaz)
        setImagePreview({ url, name: doc.filename });
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Datei konnte nicht geöffnet werden.');
    } finally {
      setIsDownloading(null);
    }
  };

  const closeImagePreview = () => {
    if (imagePreview) window.URL.revokeObjectURL(imagePreview.url);
    setImagePreview(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-10 w-10 border-2 border-primary-100 border-t-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-5">

      <div className="pb-5 border-b border-slate-200">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 mb-1.5 block">Antrag</span>
        <h1 className="text-2xl font-serif font-medium text-slate-900">Dokumente</h1>
        <p className="text-sm text-slate-500 mt-1">Festgeld-Antragsformular ausfüllen und herunterladen</p>
      </div>

      {/* ── Saved documents list ─────────────────────────────────────────── */}
      {savedDocs.length > 0 && (
        <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-200">
            <FileText className="w-4 h-4 text-primary-600" />
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Meine Dokumente ({savedDocs.length})
            </h2>
          </div>
          <div className="divide-y divide-slate-100">
            {savedDocs.map(doc => {
              const isFinancial = doc.doc_type === 'vertrag' || doc.doc_type === 'einzahlung' || !doc.doc_type;
              const isImage = doc.doc_type === 'ausweis_vs' || doc.doc_type === 'ausweis_rs' || doc.doc_type === 'unterschrift';
              const docLabel: Record<string, string> = {
                vertrag: 'Festgeld-Vertrag',
                einzahlung: 'Einzahlungsbeleg',
                ausweis_vs: 'Personalausweis – Vorderseite',
                ausweis_rs: 'Personalausweis – Rückseite',
                unterschrift: 'Unterschrift',
              };
              const title = doc.description || docLabel[doc.doc_type || ''] || doc.bank_name;
              const iconBg = isImage ? 'bg-success-50' : doc.doc_type === 'vertrag' ? 'bg-gold-50' : 'bg-primary-50';
              const iconColor = isImage ? 'text-success-700' : doc.doc_type === 'vertrag' ? 'text-gold-700' : 'text-primary-700';
              const btnColor = isImage
                ? 'text-success-700 hover:text-success-800 border-success-200 hover:bg-success-50'
                : 'text-primary-700 hover:text-primary-800 border-primary-200 hover:bg-primary-50';
              return (
              <div key={doc.id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors">
                <div className={`w-10 h-10 ${iconBg} flex items-center justify-center flex-shrink-0`}>
                  <FileText className={`w-5 h-5 ${iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900">{title}</span>
                    {doc.kunden_nr && (
                      <span className="text-xs bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 font-mono">{doc.kunden_nr}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    {isFinancial && <>
                      <span className="text-xs text-slate-500">{doc.bank_name}</span>
                      <span className="text-xs text-slate-300">·</span>
                      <span className="text-xs text-slate-500">{doc.laufzeit} Monate · {parseFloat(String(doc.zinssatz)).toFixed(2)}% p.a.</span>
                      <span className="text-xs text-slate-300">·</span>
                      <span className="text-xs font-medium text-slate-700 tabular-nums">{doc.anlagebetrag}</span>
                      {doc.iban && <>
                        <span className="text-xs text-slate-300">·</span>
                        <span className="text-xs text-slate-500 font-mono">{doc.iban.slice(0, 8)}…</span>
                      </>}
                    </>}
                    {isImage && <span className="text-xs text-slate-500">Identifikationsdokument</span>}
                    <span className="text-xs text-slate-300">·</span>
                    <span className="text-xs text-slate-400">
                      {new Date(doc.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadSaved(doc)}
                  disabled={isDownloading === doc.id}
                  className={`flex items-center gap-1.5 text-xs font-medium disabled:opacity-50 flex-shrink-0 px-3 py-1.5 border transition-colors ${btnColor}`}
                >
                  {isDownloading === doc.id
                    ? <Loader className="w-3.5 h-3.5 animate-spin" />
                    : <Download className="w-3.5 h-3.5" />
                  }
                  {isImage ? 'Bild' : 'PDF'}
                </button>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {!isAdminCreated && <>
      <div className="flex items-start gap-2 p-3 bg-primary-50 border border-primary-100">
        <Info className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-primary-800">
          Alle Felder werden direkt in das PDF übertragen. Ihre Kundennummer wird automatisch vergeben und im Antrag eingetragen.
        </p>
      </div>

      {/* ── 1. Persönliche Daten ─────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <SectionTitle icon={User} title="Persönliche Daten" />

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Anrede <span className="text-danger-600">*</span></label>
            <div className="flex gap-6 mt-2">
              {[{ v: 'herr', l: 'Herr' }, { v: 'frau', l: 'Frau' }].map(o => (
                <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="anrede" value={o.v} checked={anrede === o.v}
                    onChange={() => setAnrede(o.v)} className="text-primary-600 w-4 h-4" />
                  <span className="text-sm text-slate-700">{o.l}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Titel</label>
            <select value={titel} onChange={e => setTitel(e.target.value)} className={inputCls}>
              <option value="">— kein Titel —</option>
              <option value="Dr.">Dr.</option>
              <option value="Prof.">Prof.</option>
              <option value="Prof. Dr.">Prof. Dr.</option>
              <option value="Dipl.-Ing.">Dipl.-Ing.</option>
              <option value="Mag.">Mag.</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <Field label="Vorname" required>
            <input type="text" value={vorname} onChange={e => setVorname(e.target.value)} className={inputCls} placeholder="Max" />
          </Field>
          <Field label="Nachname" required>
            <input type="text" value={nachname} onChange={e => setNachname(e.target.value)} className={inputCls} placeholder="Mustermann" />
          </Field>
        </div>

        <div className="mb-4">
          <Field label="Straße und Hausnummer">
            <input type="text" value={strasseNr} onChange={e => setStrasseNr(e.target.value)} className={inputCls} placeholder="Musterstraße 12" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <Field label="PLZ">
            <input type="text" value={plz} onChange={e => setPlz(e.target.value)} className={inputCls} placeholder="80331" />
          </Field>
          <div className="col-span-2">
            <Field label="Ort">
              <input type="text" value={ort} onChange={e => setOrt(e.target.value)} className={inputCls} placeholder="München" />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <Field label="Nationalität">
            <input type="text" value={nationalitaet} onChange={e => setNationalitaet(e.target.value)} className={inputCls} placeholder="Deutsch" />
          </Field>
          <Field label="Geburtsdatum">
            <input type="date" value={geburtsdatum} onChange={e => setGeburtsdatum(e.target.value)} className={inputCls} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Telefon">
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="tel" value={telNr} onChange={e => setTelNr(e.target.value)} className={inputCls + " pl-9"} placeholder="+49 89 123456" />
            </div>
          </Field>
          <Field label="E-Mail-Adresse">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputCls} placeholder="max@beispiel.de" />
          </Field>
        </div>
      </div>

      {/* ── 2. Familienstand & Berufsgruppe ─────────────────────────────── */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <SectionTitle icon={User} title="Familienstand & Berufsgruppe" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Familienstand">
            <select value={familienstand} onChange={e => setFamilienstand(e.target.value)} className={inputCls}>
              {FAMILIENSTAND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Berufsgruppe">
            <select value={berufsgruppe} onChange={e => setBerufsgruppe(e.target.value)} className={inputCls}>
              {BERUFSGRUPPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
      </div>

      {/* ── 3. Referenzkonto ─────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <SectionTitle icon={CreditCard} title="Referenzkonto (IBAN)" />
        <p className="text-xs text-slate-500 -mt-2 mb-4">
          Ihr bestehendes Bankkonto, von dem der Anlagebetrag abgebucht und zurückgezahlt wird.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="IBAN">
            <input type="text" value={iban} onChange={e => setIban(e.target.value.toUpperCase())}
              className={inputCls + " font-mono"} placeholder="DE89 3704 0044 0532 0130 00" />
          </Field>
          <Field label="Name der Bank">
            <input type="text" value={refBankName} onChange={e => setRefBankName(e.target.value)} className={inputCls} placeholder="Sparkasse München" />
          </Field>
        </div>
      </div>

      {/* ── 4. Anlagedetails ─────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
        <SectionTitle icon={Building2} title="Anlagedetails" />
        <div className="mb-4">
          <Field label="Festgeldbank auswählen" required>
            <select value={selectedBank} onChange={e => setSelectedBank(e.target.value === '' ? '' : Number(e.target.value))} className={inputCls}>
              <option value="">-- Bank wählen --</option>
              {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Laufzeit (Monate)" required>
            <input type="number" min="1" max="120" value={laufzeit} onChange={e => setLaufzeit(e.target.value)} className={inputCls} placeholder="12" />
          </Field>
          <Field label="Zinssatz (% p.a.)" required>
            <input type="text" value={zinssatz} onChange={e => setZinssatz(e.target.value)} className={inputCls} placeholder="4.25" />
          </Field>
          <Field label="Anlagebetrag (€)" required>
            <input type="text" value={anlagebetrag} onChange={e => setAnlagebetrag(e.target.value)} className={inputCls} placeholder="50.000" />
          </Field>
        </div>
      </div>

      {/* ── 5. Unterschrift ──────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <div className="flex items-center justify-between pb-2 border-b border-slate-200 mb-4">
          <div className="flex items-center gap-2">
            <Pen className="w-4 h-4 text-primary-600" />
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Unterschrift</h2>
          </div>
          {hasSignature && (
            <button onClick={clearSignature} className="flex items-center gap-1.5 text-xs text-danger-600 hover:text-danger-700">
              <Trash2 className="w-3.5 h-3.5" /> Löschen
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-3">Unterschreiben Sie mit der Maus oder dem Finger im Feld unten.</p>
        <div className="border-2 border-dashed border-slate-300 overflow-hidden bg-slate-50 select-none">
          <canvas ref={canvasRef} width={700} height={140}
            style={{ aspectRatio: '5 / 1' }}
            className="w-full h-auto block cursor-crosshair touch-none"
            onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
            onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw} />
        </div>
        {!hasSignature && <p className="text-xs text-slate-400 text-center mt-1">← Hier unterschreiben →</p>}
      </div>

      {/* ── 6. Ausweisdokument ───────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <SectionTitle icon={Upload} title="Ausweisdokument hochladen" />
        <p className="text-xs text-slate-500 -mt-2 mb-4">
          Personalausweis oder Reisepass (Vorder- und Rückseite) als JPG, PNG oder PDF.
        </p>
        <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-slate-300 bg-slate-50 cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
          <Upload className="w-6 h-6 text-slate-400" />
          <span className="text-sm text-slate-600">Dateien auswählen oder hierher ziehen</span>
          <span className="text-xs text-slate-400">JPG, PNG, PDF — max. 10 MB</span>
          <input type="file" accept=".jpg,.jpeg,.png,.pdf" multiple className="hidden" onChange={handleFileUpload} disabled={isUploading} />
          {isUploading && <Loader className="w-4 h-4 animate-spin text-primary-600" />}
        </label>
        {uploadedFiles.length > 0 && (
          <ul className="mt-3 space-y-1">
            {uploadedFiles.map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="w-4 h-4 flex-shrink-0" /> {f.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Submit ───────────────────────────────────────────────────────── */}
      <Button onClick={handleGenerate} disabled={isGenerating} className="w-full flex items-center justify-center gap-2 py-3 text-base">
        {isGenerating
          ? <><Loader className="w-4 h-4 animate-spin" /> PDF wird erstellt…</>
          : <><Download className="w-4 h-4" /> Antrag als PDF erstellen & herunterladen</>
        }
      </Button>
      </>}

      {/* ── Bild-Vorschau Modal (mobil-freundlich) ──────────────────────────── */}
      {imagePreview && (
        <div
          className="fixed inset-0 z-50 bg-primary-950/85 flex flex-col items-center justify-center p-4"
          onClick={closeImagePreview}
        >
          <div className="w-full max-w-2xl flex items-center justify-between mb-3" onClick={e => e.stopPropagation()}>
            <span className="text-white text-sm font-medium truncate pr-3">{imagePreview.name}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href={imagePreview.url}
                download={imagePreview.name}
                className="flex items-center gap-1.5 text-xs font-medium text-white bg-white/15 hover:bg-white/25 px-3 py-1.5 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Speichern
              </a>
              <button
                onClick={closeImagePreview}
                className="text-white bg-white/15 hover:bg-white/25 w-8 h-8 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <img
            src={imagePreview.url}
            alt={imagePreview.name}
            className="max-w-full max-h-[80vh] object-contain bg-white"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
